import React, { FC, ReactNode, useState } from 'react';
import { FetchMoreOptions } from 'apollo-client';

import { LoaderTypes } from 'types/app';
import {
  GetReportRequestsComponent,
  GetReportRequestsQueryResult,
  ReportRequest,
  ReportRequestStatus,
} from 'api';

import { QueryManager } from 'components';

const POLL_INTERVAL = 10000;

interface IProps {
  children: (
    data: ReportRequest[],
    count: number,
    loading: boolean,
    onLoadMore: () => void,
  ) => ReactNode;
  loader?: LoaderTypes;
  limit: number;
}

const GetReportRequests: FC<IProps> = ({ children, loader, limit }) => {
  const [loading, setLoading] = useState(false);
  const [pollInterval, setPollInterval] = useState(0);

  const updateQuery: FetchMoreOptions['updateQuery'] = (prev, options) => {
    const { fetchMoreResult } = options;

    setLoading(false);

    if (!fetchMoreResult) {
      return prev;
    }

    return {
      ...prev,
      getReportRequests: {
        ...prev.getReportRequests,
        paginate: fetchMoreResult.getReportRequests.paginate,
        report_requests: [
          ...prev.getReportRequests.report_requests,
          ...fetchMoreResult.getReportRequests.report_requests,
        ],
      },
    };
  };

  function onLoadMore(queryResult: GetReportRequestsQueryResult) {
    const { data, fetchMore } = queryResult;
    const { report_requests } = data.getReportRequests;

    const startAt =
      report_requests.reduce((min: number, reportRequest) => {
        return Math.min(min, reportRequest.start_at);
      }, Number.MAX_VALUE) - 1;

    setLoading(true);

    return fetchMore({
      updateQuery,
      variables: {
        data: {
          start_at: startAt,
          limit,
        },
      },
    });
  }

  function recalculatePollInterval(reportRequests: ReportRequest[]) {
    const pollIntervalRequired = !!(
      reportRequests &&
      reportRequests.find(
        reportRequest => reportRequest.request_status === ReportRequestStatus.Generating,
      )
    );
    setPollInterval(pollIntervalRequired ? POLL_INTERVAL : 0);
  }

  return (
    <GetReportRequestsComponent
      fetchPolicy="network-only"
      variables={{ data: { limit } }}
      pollInterval={pollInterval}>
      {queryResult => (
        <QueryManager loader={loader} {...queryResult}>
          {({ data }) => {
            if (!data) {
              return null;
            }
            const { getReportRequests } = data;
            recalculatePollInterval((getReportRequests || {}).report_requests);
            if (!getReportRequests || !getReportRequests.report_requests) {
              return null;
            }

            return children(
              getReportRequests.report_requests,
              getReportRequests.count,
              loading,
              getReportRequests.paginate ? () => onLoadMore(queryResult) : null,
            );
          }}
        </QueryManager>
      )}
    </GetReportRequestsComponent>
  );
};

export default GetReportRequests;
